import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  ArchiveRange,
  CalendarStatisticsPeriod,
  PutReminderChainBody,
  ReminderChainChannel,
  ReminderChainOffsetUnit,
  ReminderChainTiming,
} from '@brimo/shared';
import { api } from '../../lib/api';
import { queryRoots } from '../../lib/events';

/**
 * Types and queries of the M5 calendar views (docs/dev/api-appointments.md: week, archive, statistics,
 * reminder chains). The week, archive and statistics live under the `appointments` root so that
 * `appointment.updated` refreshes them; the chains under `reminder-chains` (`reminder_chain.updated`).
 */

// --- week (Beratung 01) -----------------------------------------------------------------------------------

export type WeekTone = 'video' | 'on_site' | 'notary' | 'phone' | 'internal' | 'conflict';

export interface WeekItem {
  kind: 'appointment' | 'busy';
  id: string;
  startsAt: string;
  endsAt: string;
  title: string;
  subtitle: string | null;
  tone: WeekTone;
  running: boolean;
  clickable: boolean;
  href: string | null;
}

export interface WeekDay {
  date: string;
  label: string;
  today: boolean;
  unavailable: boolean;
}

export interface CalendarWeek {
  user: { id: string; shortName: string };
  start: string;
  weekLabel: string;
  statusLine: string;
  hours: { from: number; to: number };
  days: WeekDay[];
  items: WeekItem[];
}

export function useCalendarWeek(start: string | null, userId: string | null) {
  return useQuery({
    queryKey: [...queryRoots.appointments, 'week', start ?? 'current', userId ?? 'me'],
    queryFn: () => {
      const search = new URLSearchParams();
      if (start) search.set('start', start);
      if (userId) search.set('userId', userId);
      const query = search.toString();
      return api<CalendarWeek>(`/calendar/week${query ? `?${query}` : ''}`);
    },
    placeholderData: keepPreviousData,
  });
}

// --- archive (Beratung 04) --------------------------------------------------------------------------------

export type ArchiveTone = 'success' | 'danger' | 'neutral' | 'warning';

export interface ArchiveItem {
  id: string;
  dateLabel: string;
  startsAt: string;
  guest: string;
  eventTypeName: string;
  advisorShort: string;
  relation: string;
  delivery: { sent: number; opened: number; failed: number };
  status: { label: string; sub: string | null; tone: ArchiveTone };
  reviewHref: string | null;
}

export interface ArchivePage {
  advisors: Array<{ id: string; shortName: string }>;
  total: number;
  showRate: number | null;
  items: ArchiveItem[];
  nextCursor: string | null;
}

export interface ArchiveDetail {
  id: string;
  guestLine: string;
  roomLine: string | null;
  consentLine: string | null;
  sourceLine: string;
  deliveries: Array<{ at: string; atLabel: string; label: string; result: string }>;
}

export function useCalendarArchive(range: ArchiveRange, advisorId: string | null) {
  return useInfiniteQuery({
    queryKey: [...queryRoots.appointments, 'archive', range, advisorId ?? 'all'],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const search = new URLSearchParams({ range });
      if (advisorId) search.set('advisorId', advisorId);
      if (pageParam) search.set('cursor', pageParam);
      return api<ArchivePage>(`/calendar/archive?${search.toString()}`);
    },
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
  });
}

export function useArchiveDetail(id: string | null) {
  return useQuery({
    queryKey: [...queryRoots.appointments, 'archive-detail', id ?? 'none'],
    queryFn: () => api<ArchiveDetail>(`/calendar/archive/${id ?? ''}`),
    enabled: id !== null,
  });
}

// --- statistics (Beratung 09) -----------------------------------------------------------------------------

export interface CalendarStatistics {
  scopeLabel: string;
  kpis: {
    bookings: number;
    viaBookingPage: number;
    upcoming: number;
    upcomingThisWeek: number;
    cancelled: number;
    cancelledPct: number | null;
    showRate: number | null;
    attended: number;
    noShow: number;
  };
  delivery: Array<{
    channel: string;
    sent: number;
    delivered: number;
    deliveredPct: number | null;
    opened: number | null;
    openedPct: number | null;
    failed: number;
  }>;
  team: Array<{
    userId: string;
    name: string;
    email: string;
    appointments: number;
    showRate: number | null;
    roleLabel: string;
  }>;
}

export function useCalendarStatistics(period: CalendarStatisticsPeriod, enabled = true) {
  return useQuery({
    queryKey: [...queryRoots.appointments, 'statistics', period],
    queryFn: () => api<CalendarStatistics>(`/calendar/statistics?period=${period}`),
    placeholderData: keepPreviousData,
    enabled,
    retry: false,
  });
}

// --- reminder chains (Beratung 07) ------------------------------------------------------------------------

export interface ReminderChainStep {
  id: string | null;
  channel: ReminderChainChannel;
  timing: ReminderChainTiming;
  offsetValue: number;
  offsetUnit: ReminderChainOffsetUnit;
  text: string | null;
  customText: boolean;
  fixed: boolean;
  label: string;
  sublabel: string;
}

export interface ReminderChain {
  mode: 'organization' | 'own';
  eventTypeId: string | null;
  steps: ReminderChainStep[];
  placeholders: string[];
  defaultTexts: { confirmation: string; email: string; whatsapp: string; sms: string };
}

/** `null` = the organization default (`/reminder-chains/default`), else the event type's chain. */
function chainPath(eventTypeId: string | null): string {
  return eventTypeId === null ? '/reminder-chains/default' : `/event-types/${eventTypeId}/reminder-chain`;
}

export function reminderChainKey(eventTypeId: string | null) {
  return [...queryRoots.reminderChains, eventTypeId ?? 'default'] as const;
}

export function useReminderChain(eventTypeId: string | null) {
  return useQuery({
    queryKey: reminderChainKey(eventTypeId),
    queryFn: () => api<ReminderChain>(chainPath(eventTypeId)),
  });
}

export function useReminderChainMutations(eventTypeId: string | null) {
  const queryClient = useQueryClient();
  const settle = async (chain: ReminderChain) => {
    queryClient.setQueryData(reminderChainKey(eventTypeId), chain);
    await queryClient.invalidateQueries({ queryKey: [...queryRoots.eventTypes] });
  };
  const save = useMutation({
    mutationFn: (body: PutReminderChainBody) =>
      api<ReminderChain>(chainPath(eventTypeId), { method: 'PUT', body }),
    onSuccess: settle,
  });
  const remove = useMutation({
    mutationFn: () => api<ReminderChain>(chainPath(eventTypeId), { method: 'DELETE' }),
    onSuccess: settle,
  });
  return { save, remove };
}
